import { useLang } from "@/lib/i18n";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Reveal } from "@/lib/reveal";

const STATIC_DESTS = [
  { name: "Dubrovnik",     slug: "dubrovnik", from: "Split", to: "Dubrovnik", premium: true,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/67/The_walls_of_the_fortress_and_View_of_the_old_city._panorama.jpg/1920px-The_walls_of_the_fortress_and_View_of_the_old_city._panorama.jpg" },
  { name: "Plitvice Lakes", slug: "plitvice",  from: "Split", to: "Plitvice Lakes", premium: true,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/39/View_in_Plitvice_Lakes_National_Park.jpg/1920px-View_in_Plitvice_Lakes_National_Park.jpg" },
  { name: "Hvar Island Gateway", slug: "hvar", from: "Split Airport", to: "Split Ferry Port", premium: false,
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/27/View_of_Hvar_02.jpg/3840px-View_of_Hvar_02.jpg" },
];

// Quick-route grid — all use premium pricing (destinations logic).
const QUICK_ROUTES = [
  { from: "Split Airport", to: "Split City",     premium: true },
  { from: "Split Airport", to: "Trogir",         premium: true },
  { from: "Split",         to: "Zadar",          premium: true },
  { from: "Split",         to: "Šibenik",        premium: true },
  { from: "Split",         to: "Omiš",           premium: true },
  { from: "Split",         to: "Makarska",       premium: true },
];

async function fetchQuoteTotal(from, to, premium) {
  try {
    const { data } = await api.post("/quote", {
      pickup_location: from,
      dropoff_location: to,
      vehicle_type: "Comfort Class",
      pickup_time: "12:00",
      luggage: 0,
      booking_type: "transfer",
      premium_route: !!premium,
    });
    return Number(data?.total_price_eur) || null;
  } catch { return null; }
}

export default function Destinations() {
  const { t } = useLang();
  const navigate = useNavigate();
  const items = t("dest.items");
  const [prices, setPrices] = useState({}); // key: `${from}|${to}` → total

  useEffect(() => {
    let cancelled = false;
    const routes = [
      ...STATIC_DESTS.map(({ from, to, premium }) => ({ from, to, premium })),
      ...QUICK_ROUTES,
    ];
    Promise.all(routes.map(async (r) => {
      const total = await fetchQuoteTotal(r.from, r.to, r.premium);
      return [`${r.from}|${r.to}`, total];
    })).then((rs) => {
      if (cancelled) return;
      const next = {};
      for (const [k, v] of rs) if (v) next[k] = v;
      setPrices(next);
    });
    return () => { cancelled = true; };
  }, []);

  const bookRoute = (from, to, premium) => {
    const p = new URLSearchParams({ from, to });
    if (premium) p.set("premium", "1");
    navigate(`/?${p.toString()}#book`);
  };

  const priceOf = (from, to) => prices[`${from}|${to}`];

  return (
    <section id="destinations" data-testid="destinations-section" className="relative py-28 lg:py-32 px-6 lg:px-10 bg-[#050505]">
      <div className="max-w-7xl mx-auto">
        <div className="mb-14 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">{t("dest.eyebrow")}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-3xl">
              {t("dest.title")}
            </h2>
          </div>
          <Link
            to="/destinations"
            data-testid="view-all-destinations-link"
            className="hidden sm:inline-flex text-[11px] uppercase tracking-[0.25em] text-[#C5A059] hover:text-[#D4AF37] items-center gap-2 border border-[#C5A059]/30 hover:border-[#C5A059] rounded-full px-5 py-2.5 transition-colors"
          >
            View all 10 destinations →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {STATIC_DESTS.map((d, i) => {
            const x = items[i] || {};
            const price = priceOf(d.from, d.to);
            return (
              <Reveal key={d.name} delay={i * 90}>
              <button
                type="button"
                onClick={() => navigate(`/destinations/${d.slug}`)}
                data-testid={`destination-card-${i}`}
                className="group relative aspect-[4/5] w-full rounded-xl overflow-hidden border border-white/10 hover:border-[#C5A059]/40 transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 text-left hover:-translate-y-0.5"
              >
                <img
                  src={d.image}
                  alt={d.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <p className="text-[#C5A059] text-xs uppercase tracking-[0.2em] mb-2">{x.duration}</p>
                  <h3 className="font-display text-3xl text-white font-light mb-2">{d.name}</h3>
                  <p className="text-sm text-zinc-300 max-w-xs">{x.blurb}</p>
                  {price != null && (
                    <p
                      className="mt-4 text-[#C5A059] text-sm font-medium tabular-nums"
                      data-testid={`static-dest-price-${i}`}
                    >
                      €{price.toFixed(0)}
                    </p>
                  )}
                </div>
              </button>
              </Reveal>
            );
          })}
        </div>

        <div className="sm:hidden mb-10">
          <Link
            to="/destinations"
            className="block w-full text-center text-[11px] uppercase tracking-[0.25em] text-[#C5A059] border border-[#C5A059]/30 rounded-full px-5 py-3"
          >
            View all 10 destinations →
          </Link>
        </div>

        <div className="rounded-xl border border-white/10 bg-[#0d0d0d] overflow-hidden">
          <div className="px-8 py-6 border-b border-white/10 flex items-baseline justify-between">
            <h3 className="font-display text-2xl text-white font-light">{t("dest.routes_title")}</h3>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("dest.routes_sub")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {QUICK_ROUTES.map((r, i) => {
              const price = priceOf(r.from, r.to);
              return (
                <Reveal key={`${r.from}-${r.to}`} delay={i * 60} y={10}>
                <button
                  type="button"
                  data-testid={`route-row-${i}`}
                  onClick={() => bookRoute(r.from, r.to, r.premium)}
                  className="w-full px-8 py-6 border-b border-r border-white/5 hover:bg-white/[0.02] transition-colors text-left focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40 focus:ring-inset"
                >
                  <p className="text-sm text-zinc-500">{r.from}</p>
                  <p className="font-display text-lg text-white mt-0.5">→ {r.to}</p>
                  <p className="text-[#C5A059] text-sm mt-3 font-medium tabular-nums">
                    {price != null ? `€${price.toFixed(0)}` : "—"}
                  </p>
                </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
